import { useQuery } from '@tanstack/react-query';
import { Radio, ShieldCheck, Video } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardHeader } from '@/components/ui/Card';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { Alert } from '@/components/ui/Alert';
import { EmptyState, Skeleton, Spinner } from '@/components/ui/Feedback';
import { listSessions } from '@/lib/api/endpoints/sessions';
import { queryKeys } from '@/lib/query-keys';
import { formatAbsolute, formatBytes, formatDuration, formatRelative } from '@/lib/format';
import type { RemoteSession, SessionStatus } from '@/lib/api/types';

/*
  Sessions.

  Two groups, because they answer different questions: what is happening right now, and what
  happened. A single merged table buries the two live sessions among fifty finished ones.

  Requested versus granted capabilities are both shown. That difference is the audit trail of
  a customer declining part of what a technician asked for, and it is enforced at the
  endpoint rather than by the server (docs/07-identity-rbac.md I.5), so it is the one place
  the UI can show that local policy overruled the control plane.
*/

const STATUS_TONE: Readonly<Record<SessionStatus, BadgeTone>> = {
  pending: 'info',
  waiting_consent: 'warning',
  active: 'success',
  ended: 'neutral',
  denied: 'warning',
  expired: 'neutral',
  failed: 'danger',
};

const STATUS_LABEL: Readonly<Record<SessionStatus, string>> = {
  pending: 'Pending',
  waiting_consent: 'Waiting for consent',
  active: 'Active',
  ended: 'Ended',
  denied: 'Denied',
  expired: 'Expired',
  failed: 'Failed',
};

const LIVE_STATUSES: readonly SessionStatus[] = ['pending', 'waiting_consent', 'active'];

function CapabilityList({ session }: { readonly session: RemoteSession }): React.JSX.Element {
  const notGranted = session.requestedCaps.filter((cap) => !session.grantedCaps.includes(cap));
  return (
    <span className="flex flex-wrap gap-1">
      {session.grantedCaps.map((cap) => (
        <Badge key={cap} tone="accent" title="Granted by the endpoint">
          {cap}
        </Badge>
      ))}
      {notGranted.map((cap) => (
        <Badge
          key={cap}
          tone="warning"
          title="Requested by the technician and not granted at the endpoint"
        >
          {cap} (refused)
        </Badge>
      ))}
      {session.grantedCaps.length === 0 && notGranted.length === 0 ? (
        <span className="text-xs text-fg-subtle">None</span>
      ) : null}
    </span>
  );
}

function SessionRow({ session }: { readonly session: RemoteSession }): React.JSX.Element {
  const live = LIVE_STATUSES.includes(session.status);
  return (
    <li className="border-b border-border px-4 py-3 last:border-b-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm font-medium text-fg">
              {session.deviceName ?? 'No device attached yet'}
            </span>
            <Badge tone={STATUS_TONE[session.status]}>{STATUS_LABEL[session.status]}</Badge>
            <Badge tone="neutral">
              {session.mode === 'quick_support' ? 'Quick Support' : 'Unattended'}
            </Badge>
            {session.recordingPolicy === 'always' ? (
              <Badge tone="info" title="Recording is mandatory for this organization">
                <Video className="size-3" aria-hidden="true" />
                Recorded
              </Badge>
            ) : null}
          </div>
          <p className="mt-1 text-xs text-fg-muted">
            {session.organizationName}
            {session.createdByName === null ? '' : ` · ${session.createdByName}`}
            {session.customerName === null ? '' : ` · customer ${session.customerName}`}
          </p>
          <div className="mt-2">
            <CapabilityList session={session} />
          </div>
          {session.joinCode !== null ? (
            <p className="mt-2 text-xs text-fg-muted">
              Join code{' '}
              <span className="font-mono text-sm font-semibold tracking-wider text-fg">
                {session.joinCode}
              </span>{' '}
              <span className="text-fg-subtle">
                (readable only while the session is pending, then discarded)
              </span>
            </p>
          ) : null}
          {session.endReason !== null ? (
            <p className="mt-1 text-xs text-fg-subtle">Ended because: {session.endReason}</p>
          ) : null}
        </div>

        <dl className="shrink-0 text-right text-xs">
          <div>
            <dt className="inline text-fg-subtle">Started </dt>
            <dd className="inline text-fg-muted">
              {session.startedAt === null ? (
                'not yet'
              ) : (
                <time dateTime={session.startedAt} title={formatAbsolute(session.startedAt)}>
                  {formatRelative(session.startedAt)}
                </time>
              )}
            </dd>
          </div>
          {session.startedAt !== null ? (
            <div>
              <dt className="inline text-fg-subtle">Duration </dt>
              <dd className="inline text-fg-muted tabular-nums">
                {formatDuration(session.startedAt, session.endedAt)}
                {live ? ' and counting' : ''}
              </dd>
            </div>
          ) : null}
          {session.connectionType !== null ? (
            <div>
              <dt className="inline text-fg-subtle">Path </dt>
              <dd className="inline text-fg-muted">{session.connectionType}</dd>
            </div>
          ) : null}
          {session.bytesSent > 0 ? (
            <div>
              <dt className="inline text-fg-subtle">Transferred </dt>
              <dd className="inline text-fg-muted tabular-nums">
                {formatBytes(session.bytesSent + session.bytesReceived)}
              </dd>
            </div>
          ) : null}
        </dl>
      </div>
    </li>
  );
}

export function SessionsPage(): React.JSX.Element {
  const { data, isPending, isError, error } = useQuery({
    queryKey: queryKeys.sessions,
    queryFn: listSessions,
    refetchInterval: 15_000,
  });

  const sessions = data ?? [];
  const live = sessions.filter((session) => LIVE_STATUSES.includes(session.status));
  const finished = sessions.filter((session) => !LIVE_STATUSES.includes(session.status));

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Sessions"
        description="Live and recent remote sessions. Capabilities are shown as requested against granted, because the endpoint enforces its own customer policy independently of the server."
      />

      {isError ? (
        <Alert tone="danger" title="Could not load sessions" liveness="assertive">
          {error instanceof Error ? error.message : 'Unknown error.'}
        </Alert>
      ) : null}

      {isPending ? (
        <div className="flex flex-col gap-3">
          <Spinner label="Loading sessions" />
          <Card>
            <Skeleton className="h-32 w-full" />
          </Card>
        </div>
      ) : (
        <>
          <Card>
            <CardHeader
              title="Live now"
              description="Sessions that are pending, awaiting customer consent, or connected."
              action={<Badge tone={live.length === 0 ? 'neutral' : 'success'}>{live.length}</Badge>}
            />
            {live.length === 0 ? (
              <EmptyState
                icon={<Radio className="size-5" aria-hidden="true" />}
                title="No sessions in progress"
                description="Start one from a device row, or create a Quick Support session for a caller who has no agent installed."
              />
            ) : (
              <ul>
                {live.map((session) => (
                  <SessionRow key={session.id} session={session} />
                ))}
              </ul>
            )}
          </Card>

          <Card>
            <CardHeader
              title="Recent history"
              description="Every session is retained with its outcome, including the ones a customer declined."
              action={<Badge tone="neutral">{finished.length}</Badge>}
            />
            {finished.length === 0 ? (
              <EmptyState title="No session history yet" />
            ) : (
              <ul>
                {finished.map((session) => (
                  <SessionRow key={session.id} session={session} />
                ))}
              </ul>
            )}
          </Card>
        </>
      )}

      <p className="flex items-start gap-2 text-xs text-fg-subtle">
        <ShieldCheck className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        <span>
          Media is encrypted end to end between the browser and the endpoint. A relay forwards
          packets it cannot decrypt, so a session travelling over a relay is no less private
          than a direct one.
        </span>
      </p>
    </div>
  );
}
